import React, { useEffect, Fragment, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";

import { GetDepartments, UpdateDepartment } from "../../../../api/masters/departmentAPI";
import { GetItems } from "../../../../api/medicalStore/master/itemAPI";
import { GetIssueReceiveById, UpdateIssueReceive } from "../../../../api/medicalStore/transaction/issueReceiveAPI";
import { GetDepartmentIssueById, UpdateDepartmentIssue } from "../../../../api/medicalStore/transaction/departmentIssueAPI";

// Validation schema using Yup
const schema = yup.object().shape({
	vrType: yup.string().required("Voucher Type required"),
	vrNo: yup.number().required("Voucher No required"),
	vrDate: yup.date().required("Voucher Date required"),

	toDepartmentId: yup.string().required("To Department required"),

	remark: yup.string(),

	grossAmount: yup.number().required(),
	netAmount: yup.number().required(),

	itemDetails: yup.array().of(
		yup.object().shape({
			productId: yup.string().required("Item required"),
			unit: yup.number().required("Unit required"),
			quantity: yup.number().required("Quantity required"),
			rate: yup.number().required("Rate required"),
			amount: yup.number().required("Amount required"),
			batch: yup.number().required("Batch required")
		})
	)
});

const EditIssueReceiveEntry = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { id } = useParams();

	const [departments, setDepartments] = useState([]);
	const [items, setItems] = useState([]);

	const {
		register,
		control,
		setValue,
		getValues,
		handleSubmit,
		formState: { errors }
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			itemDetails: [
				{
					index: 1,
					productId: "",
					unit: 1,
					quantity: "",
					rate: "",
					amount: "",
					batch: ""
				}
			]
		}
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "itemDetails"
	});

	/* ================= ROW CALCULATION ================= */
	const calculateRow = index => {
		const qty = Number(getValues(`itemDetails.${index}.quantity`) || 0);
		const rate = Number(getValues(`itemDetails.${index}.rate`) || 0);

		const amount = qty * rate;

		setValue(`itemDetails.${index}.amount`, amount.toFixed(2));
	};

	/* ================= TOTAL CALCULATION ================= */
	const watchedItems = useWatch({ control, name: "itemDetails" });

	useEffect(() => {
		let total = 0;
		watchedItems?.forEach(i => {
			total += Number(i.amount || 0);
		});

		setValue("grossAmount", total.toFixed(2));
		setValue("netAmount", total.toFixed(2));
	}, [watchedItems, setValue]);


	const fetchData = async () => {
		try {
			const response = await GetDepartmentIssueById(id); // Make sure login function returns token
			
			if (response.success) {
				const data = response.data;
				// ================= SIMPLE FIELDS =================
				Object.keys(data).forEach(key => {
					if (key === "itemDetails") return;

					// handle date fields if any in future
					if (data[key] instanceof Date || typeof data[key] === "string") {
						if (key.includes("vrDate") || key.includes("billDate") && data[key]) {
							setValue(key, data[key].split("T")[0]);
							return;
						}
					}

					setValue(key, data[key]);
				});

				// ================= MEDICINE DETAILS (ARRAY) =================
				if (Array.isArray(data.itemDetails)) {
					data.itemDetails.forEach((item, index) => {
						Object.keys(item).forEach(field => {
							if (field === "expiryDate" && item[field]) {
								setValue(
									`itemDetails.${index}.expiryDate`,
									item[field].split("T")[0]
								);
							} else {
								setValue(
									`itemDetails.${index}.${field}`,
									item[field]
								);
							}
						});
					});
				}
			} else {
				// setError(response.message);
				showToast("error", response.message);
			}
		} catch (error) {
			// setError(error); // Handle login errors
			showToast("error", error);
		}
	};

	useEffect(() => {
		if (!id) return;
		// Fetch existing data
		const loadAllData = async () => {
			try {
				const [
					departmentRes,
					itemRes
				] = await Promise.all([
					GetDepartments(),
					GetItems()
				]);

				if (departmentRes.success) {
					setDepartments(departmentRes.data);
				} else {
					showToast("error", departmentRes.message);
				}

				if (itemRes.success) {
					setItems(itemRes.data);
				} else {
					showToast("error", itemRes.message);
				}

				// ✅ CALL AFTER ALL ABOVE ARE DONE
				fetchData();
			} catch (error) {
				console.error("Error loading master data:", error);
			}
		};
		loadAllData();
	}, [id]);


	const onSubmit = async data => {
		try {
			dispatch({ type: "LOADER_START" });

			const response = await UpdateDepartmentIssue(id, data);
			if (response.success) {
				showToast("success", response.message);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error);
		} finally {
			dispatch({ type: "LOADER_STOP" });
		}
	};

	return (
		<Fragment>
      <Breadcrumb parent="Medical Store" title="Edit Department Issue" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Edit Department Issue Entry</h5>
            <Link
              to={`/medical-store/transaction/department-issue-entry/list`}
            >
              <button className="btn btn-primary">List Department Issue</button>
            </Link>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="card-body">
              {/* HEADER */}
              <div className="row">
                <div className="col-3">
                  <label>Voucher Type</label>
                  <input className="form-control" {...register("vrType")} />
                </div>

                <div className="col-3">
                  <label>Voucher No</label>
                  <input
                    type="number"
                    className="form-control"
                    {...register("vrNo")}
                  />
                </div>

                <div className="col-3">
                  <label>Voucher Date</label>
                  <input
                    type="date"
                    className="form-control"
                    {...register("vrDate")}
                  />
                </div>

                <div className="col-3">
                  <label>To Department</label>
                  <select
                    className="form-control"
                    {...register("toDepartmentId")}
                  >
                    <option value="">Select</option>
                    {departments.map(d => (
                      <option key={d._id} value={d._id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-6">
                  <label>Remark</label>
                  <input className="form-control" {...register("remark")} />
                </div>
              </div>

              {/* ITEMS */}
              <hr />
              <h6>Item Details</h6>

              {fields.map((item, index) => (
                <div className="row border p-2 mb-2" key={item.id}>
                  <div className="col-2">
                    <label>Item</label>
                    <select
                      className="form-control"
                      {...register(`itemDetails.${index}.productId`)}
                    >
                      <option value="">Select</option>
                      {items.map(i => (
                        <option key={i._id} value={i._id}>
                          {i.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-2">
                    <label>Unit</label>
                    <input
                      type="number"
                      className="form-control"
                      {...register(`itemDetails.${index}.unit`)}
                    />
                  </div>

                  <div className="col-2">
                    <label>Qty</label>
                    <input
                      type="number"
                      className="form-control"
                      {...register(`itemDetails.${index}.quantity`, {
                        onChange: () => calculateRow(index)
                      })}
                    />
                  </div>

                  <div className="col-2">
                    <label>Rate</label>
                    <input
                      type="number"
                      className="form-control"
                      {...register(`itemDetails.${index}.rate`, {
                        onChange: () => calculateRow(index)
                      })}
                    />
                  </div>

                  <div className="col-2">
                    <label>Batch</label>
                    <input
                      type="number"
                      className="form-control"
                      {...register(`itemDetails.${index}.batch`)}
                    />
                  </div>

                  <div className="col-2">
                    <label>Amount</label>
                    <input
                      type="number"
                      className="form-control"
                      {...register(`itemDetails.${index}.amount`)}
                      readOnly
                    />
                  </div>

                  <div className="col-1 text-center">
                    <button
                      type="button"
                      className="btn btn-danger mt-4"
                      onClick={() => remove(index)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  append({
                    index: fields.length + 1,
                    productId: "",
                    unit: 1,
                    quantity: "",
                    rate: "",
                    amount: "",
                    batch: ""
                  })
                }
              >
                + Add Item
              </button>

              {/* TOTALS */}
              <hr />
              <div className="row">
                <div className="col-3">
                  <label>Gross Amount</label>
                  <input
                    className="form-control"
                    {...register("grossAmount")}
                    readOnly
                  />
                </div>
                <div className="col-3">
                  <label>Net Amount</label>
                  <input
                    className="form-control"
                    {...register("netAmount")}
                    readOnly
                  />
                </div>
              </div>
            </div>

            <div className="card-footer text-end">
              <button className="btn btn-primary">Submit</button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
	);
};

export default EditIssueReceiveEntry;
